import React, { useEffect, useState } from "react";
import { api, formatError, isValidEmail } from "@/lib/api";
import { Handshake, X, Send, CheckCircle2 } from "lucide-react";
import { toast } from "@/lib/toast";

export const COLLAB_INQUIRY_EVENT = "os:open-collaborator-inquiry";
export const openCollaboratorInquiry = () => window.dispatchEvent(new CustomEvent(COLLAB_INQUIRY_EVENT));

// "Become a Collaborator" contact/apply flow. Stores the inquiry in Admin → Feedback (tagged
// "Collaborator inquiry") AND emails the owner so a prospective partner is never left unread.
export default function CollaboratorInquiryModal() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", link: "", message: "" });

  useEffect(() => {
    const handler = () => { setForm({ name: "", email: "", link: "", message: "" }); setSent(false); setOpen(true); };
    window.addEventListener(COLLAB_INQUIRY_EVENT, handler);
    return () => window.removeEventListener(COLLAB_INQUIRY_EVENT, handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit = form.name.trim().length >= 2 && isValidEmail(form.email) && form.message.trim().length >= 10 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Add your name, a valid email, and a short message (10+ characters)."); return; }
    setBusy(true);
    try {
      await api.post("/collaborator-inquiry", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        link: form.link.trim() || undefined,
        message: form.message.trim(),
      });
      setSent(true);
      setTimeout(() => setOpen(false), 1600);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't send that — please try again.");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const inputCls = "mt-1 w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 text-[#E8E8EA] placeholder:text-[#6B6D70] text-sm transition-colors";
  const label = "font-mono text-xs uppercase tracking-wide text-[#8B8D90]";

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="collaborator-inquiry-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full sm:max-w-md bg-[#111213] border border-[#2E2F31] animate-fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2F31]">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2 text-[#E8E8EA]">
            <Handshake className="w-5 h-5 text-olive-bright" /> Become a Partner
          </h2>
          <button onClick={() => setOpen(false)} data-testid="collaborator-inquiry-close" className="text-[#8B8D90] hover:text-[#E8E8EA] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="px-5 py-10 text-center" data-testid="collaborator-inquiry-sent">
            <CheckCircle2 className="w-10 h-10 text-olive-bright mx-auto" />
            <p className="mt-3 font-heading text-lg text-[#E8E8EA]">Thanks — we've got it.</p>
            <p className="text-xs font-mono text-[#8B8D90] mt-1">We'll reach out about partnering personally.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-4 space-y-4">
            <p className="text-[13px] text-[#8B8D90] font-body leading-relaxed">
              Creators, ranges, clubs, and community builders — tell us how you'd like to work together and we'll be in touch.
            </p>
            <div>
              <label htmlFor="ci-name" className={label}>Your name</label>
              <input id="ci-name" data-testid="collaborator-inquiry-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" />
            </div>
            <div>
              <label htmlFor="ci-email" className={label}>Email</label>
              <input id="ci-email" data-testid="collaborator-inquiry-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" autoCapitalize="none" spellCheck={false} />
            </div>
            <div>
              <label htmlFor="ci-link" className={label}>Website / social link <span className="normal-case text-[#6B6D70]">(optional)</span></label>
              <input id="ci-link" data-testid="collaborator-inquiry-link" value={form.link} onChange={set("link")} className={inputCls} placeholder="instagram.com/yourhandle" autoCapitalize="none" spellCheck={false} />
            </div>
            <div>
              <label htmlFor="ci-msg" className={label}>How would you like to partner?</label>
              <textarea id="ci-msg" data-testid="collaborator-inquiry-message" value={form.message} onChange={set("message")} rows={4}
                placeholder="Tell us about your audience, range, club, or the kind of partnership you have in mind."
                className={`${inputCls} resize-none`} />
            </div>
            <button data-testid="collaborator-inquiry-submit" disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-base py-3 transition-colors">
              {busy ? "Sending..." : <>Send inquiry <Send className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
